import {htmlToText, textToHtml} from '@frontapp/editor-html';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {
  autoGenerateDraft,
  draftAssistantLanguageLabels,
  DraftAssistantLanguageOptionsEnum,
  draftAssistantTip,
  draftAssistantToneLabels,
  DraftAssistantToneOptionsEnum,
} from '../../../../../../libs/shared/core/src/helpers/ai/composeHelpers';
import {Trans, useI18n} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  DraftMessageModel,
  TimelineMessageModel,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {collectEnumValues} from '../../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {HttpResponseError} from '../../../../../../libs/shared/core/src/services/http/httpError';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {DialogButtonGroup} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {helpCenterUrl, navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {DraftAssistantLoader} from './draftAssistantLoader';

/*
 * Props.
 */

interface DraftAssistantDropdownProps extends DropdownProps {
  onDraftAssistantInsertDraft: (arg: string) => void;
  conversation: ConversationModel | null;
  draftMessage: DraftMessageModel;
  previousMessage: TimelineMessageModel | null;
  author: TeammateModel | undefined;
  companyUrl: string;
}

/*
 * Constants.
 */

const configurationDropdownWidth = 300;

/*
 * Styles.
 */

const StyledLoaderContainerDiv = styled.div`
  height: 270px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTextDiv = styled.div`
  padding: 18px 20px;
  line-height: 22px;
`;

const StyledDialogContentDiv = styled.div`
  padding: 18px 20px 8px 20px;
`;

const StyledDescriptionDiv = styled.div`
  padding-bottom: 20px;
  line-height: 22px;
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

export const DraftAssistantDropdown: FC<DraftAssistantDropdownProps> = (props) => {
  const dispatch = useAppDispatch();

  const {i18n} = useI18n();

  const {author, companyUrl, conversation, draftMessage, previousMessage, ...dropdownProps} = props;

  const [tone, setTone] = useState<DraftAssistantToneOptionsEnum>(DraftAssistantToneOptionsEnum.PROFESSIONAL);
  const [language, setLanguage] = useState<DraftAssistantLanguageOptionsEnum>(
    DraftAssistantLanguageOptionsEnum.MATCH_LANGUAGE,
  );
  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADED);
  const [error, setError] = useState<HttpResponseError | undefined>(undefined);

  // Disabled state: disable AI compose if we don't have any context to compose from (no replying message and empty draft)
  const draftIsEmpty = draftMessage.blurb.length === 0;
  if (!conversation || (draftIsEmpty && !previousMessage)) {
    return (
      <Dropdown {...dropdownProps} width={configurationDropdownWidth}>
        <DropdownHeader title={<Trans id="draft-assistant-magiccompose">Compose with AI</Trans>} />
        <StyledTextDiv>
          <Trans id="draft-assistant-autocompose-start-draft">Start a draft to unlock AI compose.</Trans>
        </StyledTextDiv>
      </Dropdown>
    );
  }

  const onClickSave = async () => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    const draft = await autoGenerateDraft({
      companyUrl,
      conversationId: conversation.id,
      tone,
      language,
      recipients: draftMessage.recipients,
      draftText: draftMessage.html ? htmlToText(draftMessage.html) : '',
      senderFirstName: author?.givenName,
    }).catch((err) => {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      if (err instanceof HttpResponseError) {
        setError(err);
      }
    });

    // Something went wrong - set loading state to failed so user can retry.
    if (!draft) {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      return;
    }

    props.onDraftAssistantInsertDraft(textToHtml(draft));
    setLoadingStatus(AsyncStatusesEnum.LOADED);
    setError(undefined);

    trackInterfaceEvent(InterfaceEventsEnum.AI_COMPOSE_INSERT, {
      tone,
      message_uid: draftMessage.uid,
    });

    if (dropdownProps.onRequestClose) {
      dropdownProps.onRequestClose();
    }
  };

  const renderDropdownContent = () => {
    // Loading and error states
    if (loadingStatus === AsyncStatusesEnum.LOADING || loadingStatus === AsyncStatusesEnum.FAILED) {
      return (
        <StyledLoaderContainerDiv>
          <DraftAssistantLoader
            error={error}
            loadingStatus={loadingStatus}
            loadingMessage={
              <Trans id="draft-assistant-autocompose-async-loading">Generating your draft&hellip;</Trans>
            }
            onRetryClick={onClickSave}
          />
        </StyledLoaderContainerDiv>
      );
    }

    return (
      <>
        <StyledDialogContentDiv>
          <StyledDescriptionDiv>{i18n._(draftAssistantTip)}</StyledDescriptionDiv>
          <DropdownBoxCoordinator
            label={<Trans id="draft-assistant-autocompose-tone">Tone</Trans>}
            value={tone}
            renderDropdown={({anchorWidth, anchor, onRequestClose}) => (
              <SimpleDropdown width={anchorWidth} anchor={anchor} onRequestClose={onRequestClose}>
                {collectEnumValues(DraftAssistantToneOptionsEnum).map((value) => (
                  <SimpleDropdownItem key={value} onSelect={() => setTone(value)}>
                    {i18n._(draftAssistantToneLabels[value])}
                  </SimpleDropdownItem>
                ))}
              </SimpleDropdown>
            )}
          />
          <DropdownBoxCoordinator
            label={<Trans id="draft-assistant-autocompose-language">Language</Trans>}
            value={language}
            renderDropdown={({anchorWidth, anchor, onRequestClose}) => (
              <SimpleDropdown width={anchorWidth} anchor={anchor} onRequestClose={onRequestClose}>
                {collectEnumValues(DraftAssistantLanguageOptionsEnum).map((value) => (
                  <SimpleDropdownItem key={value} onSelect={() => setLanguage(value)}>
                    {i18n._(draftAssistantLanguageLabels[value])}
                  </SimpleDropdownItem>
                ))}
              </SimpleDropdown>
            )}
          />
        </StyledDialogContentDiv>
        <DialogButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={() => dispatch(navigateToLinkAction(`${helpCenterUrl}/t/60hc3w8`))}>
            <StyledButtonContent>
              <Trans id="draft-assistant-dropdown-learn-more">Learn more</Trans>
              <StyledIcon name="externalLinkSmall" />
            </StyledButtonContent>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onClickSave}>
            <Trans id="draft-assistant-generate-button">Generate</Trans>
          </Button>
        </DialogButtonGroup>
      </>
    );
  };

  return (
    <Dropdown {...dropdownProps} width={configurationDropdownWidth} placement="top-start">
      <DropdownHeader title={<Trans id="draft-assistant-magiccompose">Compose with AI</Trans>} />
      {renderDropdownContent()}
    </Dropdown>
  );
};
